import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { aiAttributeApi } from './aiAttribute.api';
import { AttributeItem } from '../aiAttributeList.types';
import { useShowToast } from '@/hooks';

const errorsMap = {
  err_bulk_review_in_progress:
    'Please wait for the bulk review to complete before validating attributes',
};

export const useAiScoreAttributeMutations = ({
  jobData,
  setFinalValidationResult,
}: {
  jobData: { id: string };
  setFinalValidationResult?: (result: any) => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useShowToast();

  // 1.1 validate attributes
  const validateAttributesMutation = useMutation({
    mutationFn: ({
      attributes,
      shouldSave = false,
      jobId,
      retrigger = false,
    }: {
      attributes: AttributeItem[];
      shouldSave?: boolean;
      jobId?: string;
      pollingKey?: string;
      retrigger?: boolean;
    }) => {
      return aiAttributeApi.validateAttributes(jobId, {
        ai_scoring: {
          should_save: shouldSave,
          retrigger,
          attributes: attributes.map((attr) => {
            const { _isNewAttribute, ...rest } = attr;
            return rest;
          }),
        },
      });
    },
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: (response) => {
      const { data = {}, errors = [] } = response?.data ?? {};

      if (errors.length > 0) {
        console.log('Validation errors:', errors);
        toast({
          title:
            typeof errors?.[0] === 'string'
              ? errorsMap?.[errors?.[0]]
                ? errorsMap?.[errors?.[0]]
                : errors?.[0]
              : 'Validation errors',
          status: 'error',
        });
      }

      if (Object.keys(data).length === 0) {
        setIsLoading(false);
        return;
      }

      const { status, result_key } = data;
      if (status === 'pending') {
        validateAttributesStatusMutation.mutate(result_key);
      } else {
        setIsLoading(false);
        // Call the callback with the final result for immediate (non-pending) responses
        if (setFinalValidationResult) {
          setFinalValidationResult({
            success: true,
            attributes: data?.attributes,
            errors: errors || [],
          });
        }
      }
    },
    onError: (error) => {
      console.error('Validation failed:', error);
      setIsLoading(false);
      // Call the callback with error result
      if (setFinalValidationResult) {
        setFinalValidationResult({
          success: false,
          attributes: undefined,
          errors: [error],
        });
      }
    },
  });

  // 1.2.poll for validate attributes status, this will give us both attributes and errors
  const validateAttributesStatusMutation = useMutation({
    mutationFn: (resultKey: string) => {
      return aiAttributeApi.validateAttributesStatus(jobData?.id, resultKey);
    },
    onSuccess: (response) => {
      const { data, errors } = response.data;
      const { status, result_key } = data;
      if (status === 'success') {
        setIsLoading(false);

        // Call the callback with the final validation result
        if (setFinalValidationResult) {
          setFinalValidationResult({
            success: true,
            attributes: data?.attributes,
            errors: errors || [],
          });
        }

        // Show toast if any attribute is invalid or errors exist
        const hasInvalid =
          Array.isArray(data?.attributes) &&
          data.attributes.some(
            (attr) => attr.is_valid === false && attr.valid_type === 'error'
          );
        if (hasInvalid || (errors && errors.length > 0)) {
          // hiding toast for now
          // toast({
          //   title: 'Invalid attributes',
          //   status: 'error',
          //   description:
          //     'Some attributes are invalid. Please review and fix the errors.',
          // });
        }
      } else if (status === 'pending') {
        setTimeout(() => {
          validateAttributesStatusMutation.mutate(result_key);
        }, 1000);
      } else {
        setIsLoading(false);
        toast({
          title: 'Error validating attributes',
          description: 'Please try again after some time',
          status: 'error',
        });
        console.log(
          'data in onError of validateAttributesStatusMutation',
          data
        );
        // Call the callback with failure result
        if (setFinalValidationResult) {
          setFinalValidationResult({
            success: false,
            attributes: data?.attributes,
            errors: errors || [],
          });
        }
      }
    },
    onError: (error) => {
      setIsLoading(false);
      // Call the callback with error result
      if (setFinalValidationResult) {
        setFinalValidationResult({
          success: false,
          attributes: undefined,
          errors: [error],
        });
      }
    },
  });

  return {
    isLoading,
    validateAttributesMutation,
    validateAttributesStatusMutation,
  };
};
